import React, { useEffect } from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { trackPageView } from "./lib/tracker";

// Context Providers
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { WishlistProvider } from "./context/WishlistContext";

// Layout Components
import { Header } from "./components/layout/Header";
import { Footer } from "./components/layout/Footer";
import { CartDrawer } from "./components/layout/CartDrawer";
import { WhatsAppButton } from "./components/layout/WhatsAppButton";
import { NewsletterPopup } from "./components/layout/NewsletterPopup";

// Pages
import HomePage from "./pages/HomePage";
import ShopPage from "./pages/ShopPage";
import ProductPage from "./pages/ProductPage";
import CheckoutPage from "./pages/CheckoutPage";
import OrderSuccessPage from "./pages/OrderSuccessPage";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import SizeGuidePage from "./pages/SizeGuidePage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import WishlistPage from "./pages/WishlistPage";
import OrdersPage from "./pages/OrdersPage";
import AccountPage from "./pages/AccountPage";
import LoyaltyPage from "./pages/VIPProfilePage";
import AffiliatePage from "./pages/AffiliatePage";
import AdminPage, { 
  AdminDashboard, 
  AdminProducts, 
  AdminProductForm, 
  AdminOrders,
  AdminPromos,
  AdminAffiliates,
  AdminCategories
} from "./pages/AdminPage";
import AdminCampaigns from "./pages/AdminCampaigns";
import AdminShipping from "./pages/AdminShipping";
import AdminMediaLibrary from "./pages/AdminMediaLibrary";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminShopifyImport from "./pages/AdminShopifyImport";
import AdminOrderDetail from "./pages/AdminOrderDetail";
import AdminErrors from "./pages/AdminErrors";
import AdminSettings from "./pages/AdminSettings";
import AdminDocs from "./pages/AdminDocs";
import TrackingPage from "./pages/TrackingPage";
import AdminSecurity from "./pages/AdminSecurity";
import VerifyTotpPage from "./pages/VerifyTotpPage";
import RequireAdminWithStepUp from "./components/admin/RequireAdminWithStepUp";
import AdminsTab from "./components/admin/AdminsTab";

import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import CGVPage from "./pages/CGVPage";
import MentionsLegalesPage from "./pages/MentionsLegalesPage";

// Layout wrapper for public pages
const PublicLayout = ({ children }) => {
  return (
    <>
      <Header />
      <main className="min-h-screen">
        {children}
      </main>
      <Footer />
      <CartDrawer />
      <WhatsAppButton />
      <NewsletterPopup />
    </>
  );
};

// App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();

  // Track page views
  useEffect(() => {
    if (!location.pathname.startsWith('/bloom-gestion-secure')) {
      trackPageView(location.pathname);
    }
  }, [location.pathname]);

  // Capture affiliate ?ref= globally on EVERY page (not just /checkout).
  // Real affiliate traffic lands on homepage/product page, then navigates to
  // checkout — by which time the URL param is gone unless we persist it here.
  useEffect(() => {
    const ref = new URLSearchParams(location.search).get('ref');
    if (ref) {
      localStorage.setItem('bloom_affiliate_ref', ref.trim().toUpperCase());
    }
  }, [location.search]);

  // CRITICAL: Check for session_id in URL fragment BEFORE rendering routes
  // This prevents race conditions with auth checks
  if (location.hash?.includes('session_id=')) {
    return <AuthCallbackPage />;
  }

  return (
    <Routes>
      {/* Admin Routes - Protected by OAuth+TOTP step-up gate (post-cutover) */}
      <Route path="/bloom-gestion-secure" element={<RequireAdminWithStepUp><AdminPage /></RequireAdminWithStepUp>}>
        <Route index element={<AdminDashboard />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="products/new" element={<AdminProductForm />} />
        <Route path="products/:productId" element={<AdminProductForm />} />
        <Route path="categories" element={<AdminCategories />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="orders/:orderId" element={<AdminOrderDetail />} />
        <Route path="promos" element={<AdminPromos />} />
        <Route path="campaigns" element={<AdminCampaigns />} />
        <Route path="shipping" element={<AdminShipping />} />
        <Route path="security" element={<AdminSecurity />} />
        <Route path="admins" element={<AdminsTab />} />
        <Route path="affiliates" element={<AdminAffiliates />} />
        <Route path="media" element={<AdminMediaLibrary />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="shopify-import" element={<AdminShopifyImport />} />
        <Route path="docs" element={<AdminDocs />} />
        <Route path="errors" element={<AdminErrors />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>

      {/* OAuth+TOTP step-up page (admin login flow) */}
      <Route path="/verify-totp" element={<VerifyTotpPage />} />

      {/* Auth Callback */}
      <Route path="/auth/callback" element={<AuthCallbackPage />} />

      {/* Public Routes */}
      <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
      <Route path="/shop" element={<PublicLayout><ShopPage /></PublicLayout>} />
      <Route path="/product/:productId" element={<PublicLayout><ProductPage /></PublicLayout>} />
      <Route path="/checkout" element={<PublicLayout><CheckoutPage /></PublicLayout>} />
      <Route path="/order-success" element={<PublicLayout><OrderSuccessPage /></PublicLayout>} />
      <Route path="/size-guide" element={<PublicLayout><SizeGuidePage /></PublicLayout>} />
      <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
      <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
      <Route path="/wishlist" element={<PublicLayout><WishlistPage /></PublicLayout>} />
      <Route path="/orders" element={<PublicLayout><OrdersPage /></PublicLayout>} />
      <Route path="/account" element={<PublicLayout><AccountPage /></PublicLayout>} />
      <Route path="/loyalty" element={<PublicLayout><LoyaltyPage /></PublicLayout>} />
      <Route path="/affiliate" element={<PublicLayout><AffiliatePage /></PublicLayout>} />
      <Route path="/politique-confidentialite" element={<PublicLayout><PrivacyPolicyPage /></PublicLayout>} />
      <Route path="/cgv" element={<PublicLayout><CGVPage /></PublicLayout>} />
      <Route path="/mentions-legales" element={<PublicLayout><MentionsLegalesPage /></PublicLayout>} />
      <Route path="/suivi" element={<PublicLayout><TrackingPage /></PublicLayout>} />

      {/* 404 - Redirect to home */}
      <Route path="*" element={<PublicLayout><HomePage /></PublicLayout>} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <WishlistProvider>
            <AppRouter />
            <Toaster 
              position="top-center" 
              toastOptions={{
                style: {
                  fontFamily: 'Poppins, sans-serif',
                  borderRadius: '0',
                }
              }}
            />
          </WishlistProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
